import React from 'react';
import { ChevronDown, Users, UserCheck, AlertTriangle, CheckCircle } from 'lucide-react';

const FormFunnelAnalytics = ({ data }) => {
  const {
    total_views,
    total_starts,
    total_completions,
    completion_rate,
    step_analytics
  } = data;

  // Calculate funnel stages
  const funnelStages = [
    { label: 'Views', count: total_views, color: 'bg-blue-500', icon: Users },
    { label: 'Started', count: total_starts, color: 'bg-purple-500', icon: UserCheck },
    { label: 'Completed', count: total_completions, color: 'bg-green-500', icon: CheckCircle }
  ];

  // Calculate step widths for visual funnel
  const maxCount = Math.max(total_views, total_starts, total_completions);
  
  // Health status based on completion rate
  const getHealthStatus = (rate) => {
    if (rate >= 80) return { status: 'Excellent', color: 'text-green-600', bg: 'bg-green-100' };
    if (rate >= 60) return { status: 'Good', color: 'text-yellow-600', bg: 'bg-yellow-100' };
    if (rate >= 40) return { status: 'Needs Attention', color: 'text-orange-600', bg: 'bg-orange-100' };
    return { status: 'Critical', color: 'text-red-600', bg: 'bg-red-100' };
  };

  const healthStatus = getHealthStatus(completion_rate);

  return (
    <div className="w-full mx-auto p-6 bg-white rounded-lg shadow-lg">
      {/* Header with overall health */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Form Performance</h2>
          <p className="text-gray-600">How well your form converts visitors</p>
        </div>
        <div className={`px-4 py-2 rounded-full ${healthStatus.bg}`}>
          <span className={`font-semibold ${healthStatus.color}`}>
            {healthStatus.status}
          </span>
        </div>
      </div>

      {/* Main Funnel Visualization */}
      <div className="mb-8">
        <h3 className="text-lg font-semibold mb-4 text-gray-800">Conversion Funnel</h3>
        <div className="space-y-4">
          {funnelStages.map((stage, index) => {
            const width = maxCount > 0 ? (stage.count / maxCount) * 100 : 0;
            const Icon = stage.icon;
            
            return (
              <div key={stage.label} className="relative">
                <div className="flex items-center mb-2">
                  <Icon className="w-5 h-5 mr-2 text-gray-600" />
                  <span className="font-medium text-gray-700">{stage.label}</span>
                  <span className="ml-auto text-2xl font-bold text-gray-800">
                    {stage.count}
                  </span>
                </div>
                
                {/* Visual bar */}
                <div className="relative h-8 bg-gray-100 rounded-lg overflow-hidden">
                  <div 
                    className={`h-full ${stage.color} transition-all duration-1000 ease-out flex items-center justify-end pr-3`}
                    style={{ width: `${width}%` }}
                  >
                    <span className="text-white font-semibold text-sm">
                      {width > 20 ? `${stage.count}` : ''}
                    </span>
                  </div>
                </div>
                
                {/* Drop-off indicator */}
                {index < funnelStages.length - 1 && (
                  <div className="flex items-center justify-center my-2">
                    <div className="flex items-center text-gray-500">
                      <ChevronDown className="w-4 h-4" />
                      <span className="text-sm ml-1">
                        -{funnelStages[index].count - funnelStages[index + 1].count} dropped off
                      </span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Key Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-4 rounded-lg">
          <div className="text-sm text-blue-600 font-medium">Completion Rate</div>
          <div className="text-3xl font-bold text-blue-800">{completion_rate}%</div>
          <div className="text-xs text-blue-600">
            {total_completions} out of {total_starts} starters
          </div>
        </div>
        
        <div className="bg-gradient-to-r from-green-50 to-green-100 p-4 rounded-lg">
          <div className="text-sm text-green-600 font-medium">Start Rate</div>
          <div className="text-3xl font-bold text-green-800">
            {total_views > 0 ? Math.round((total_starts / total_views) * 100) : 0}%
          </div>
          <div className="text-xs text-green-600">
            {total_starts} out of {total_views} viewers
          </div>
        </div>
        
        <div className="bg-gradient-to-r from-purple-50 to-purple-100 p-4 rounded-lg">
          <div className="text-sm text-purple-600 font-medium">Overall Success</div>
          <div className="text-3xl font-bold text-purple-800">
            {total_views > 0 ? Math.round((total_completions / total_views) * 100) : 0}%
          </div>
          <div className="text-xs text-purple-600">
            End-to-end conversion
          </div>
        </div>
      </div>

      {/* Step-by-Step Breakdown */}
      <div>
        <h3 className="text-lg font-semibold mb-4 text-gray-800">Step Performance</h3>
        <div className="space-y-3">
          {step_analytics.map((step, index) => {
            const dropOffRate = step.drop_off_rate;
            const isProblematic = dropOffRate > 30;
            
            return (
              <div 
                key={step.step_id}
                className={`p-4 rounded-lg border-2 ${isProblematic ? 'border-red-200 bg-red-50' : 'border-gray-200 bg-gray-50'}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="w-8 h-8 bg-gray-600 text-white rounded-full flex items-center justify-center font-semibold text-sm mr-3">
                      {index + 1}
                    </div>
                    <div>
                      <div className="font-medium text-gray-800">
                        Step {index + 1}
                      </div>
                      <div className="text-sm text-gray-600">
                        {step.starts} started → {step.completions} completed
                      </div>
                    </div>
                  </div>
                  
                  <div className="text-right">
                    {isProblematic && <AlertTriangle className="w-5 h-5 text-red-500 inline mr-2" />}
                    <span className={`text-lg font-bold ${isProblematic ? 'text-red-600' : 'text-gray-800'}`}>
                      {dropOffRate > 0 ? `${dropOffRate}%` : '0%'} drop-off
                    </span>
                  </div>
                </div>
                
                {isProblematic && (
                  <div className="mt-2 text-sm text-red-700 font-medium">
                    ⚠️ High drop-off detected - consider reviewing this step
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Quick Insights */}
      <div className="mt-8 p-4 bg-blue-50 rounded-lg border border-blue-200">
        <h4 className="font-semibold text-blue-800 mb-2">💡 Quick Insights</h4>
        <div className="text-sm text-blue-700 space-y-1">
          {completion_rate === 100 && <div>🎉 Perfect completion rate! All starters finished the form.</div>}
          {completion_rate < 50 && <div>📉 Low completion rate - consider simplifying your form or reducing steps.</div>}
          {total_starts / total_views < 0.5 && <div>👀 Many viewers aren't starting - your form introduction might need improvement.</div>}
          {step_analytics.some(step => step.drop_off_rate > 50) && <div>🚨 One or more steps have high drop-off rates - review those steps for complexity.</div>}
        </div>
      </div>
    </div>
  );
};

export default FormFunnelAnalytics;
